import { ReadinessModel, RouteClass, } from "../contracts/common.js";
import {} from "../contracts/result.js";
import { expectArray, expectBoolean, expectNumber, expectObject, expectOptionalBoolean, expectOptionalNumber, expectOptionalString, expectString, parseIndexedPage, readField, readOptionalField, } from "../internal/json.js";
import { encodePathSegment, createQuery } from "../internal/query.js";
import { requestJsonResult } from "../internal/transport.js";
const TRANSACTIONS_ROUTE = {
    method: "GET",
    name: "transactions",
    path: "/api/v1/txs",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.INDEXED,
};
const TRANSACTION_ROUTE = {
    method: "GET",
    name: "transaction",
    path: "/api/v1/tx/{txid}",
    readinessModel: ReadinessModel.INDEXED,
    routeClass: RouteClass.INDEXED_OVERLAY,
};
/**
 * Public route descriptors for transaction routes.
 */
export const transactionRouteDescriptors = {
    detail: TRANSACTION_ROUTE,
    list: TRANSACTIONS_ROUTE,
};
export function parseTransactionTags(value, path) {
    const object = expectObject(value, path);
    return {
        mined: expectBoolean(readField(object, "mined", path), `${path}.mined`),
        coinbase: expectBoolean(readField(object, "coinbase", path), `${path}.coinbase`),
    };
}
function parseTransactionLiveTags(value, path) {
    const object = expectObject(value, path);
    const mempool = expectOptionalBoolean(readOptionalField(object, "mempool"), `${path}.mempool`);
    const islock = expectOptionalBoolean(readOptionalField(object, "islock"), `${path}.islock`);
    return {
        ...(mempool == null ? {} : { mempool }),
        ...(islock == null ? {} : { islock }),
    };
}
function parseTransactionLive(value, path) {
    const object = expectObject(value, path);
    const tagsValue = readOptionalField(object, "tags");
    return {
        ...(tagsValue == null ? {} : { tags: parseTransactionLiveTags(tagsValue, `${path}.tags`) }),
    };
}
function parseTransactionScriptSig(value, path) {
    const object = expectObject(value, path);
    return {
        hex: expectString(readField(object, "hex", path), `${path}.hex`),
    };
}
function parseTransactionScriptPubKey(value, path) {
    const object = expectObject(value, path);
    return {
        hex: expectString(readField(object, "hex", path), `${path}.hex`),
    };
}
function parseTransactionInput(value, path) {
    const object = expectObject(value, path);
    const address = expectOptionalString(readOptionalField(object, "address"), `${path}.address`);
    const valueAmount = expectOptionalNumber(readOptionalField(object, "value"), `${path}.value`);
    const txid = expectOptionalString(readOptionalField(object, "txid"), `${path}.txid`);
    const vout = expectOptionalNumber(readOptionalField(object, "vout"), `${path}.vout`);
    const coinbase = expectOptionalString(readOptionalField(object, "coinbase"), `${path}.coinbase`);
    return {
        n: expectNumber(readField(object, "n", path), `${path}.n`),
        scriptSig: parseTransactionScriptSig(readField(object, "scriptSig", path), `${path}.scriptSig`),
        sequence: expectNumber(readField(object, "sequence", path), `${path}.sequence`),
        ...(address == null ? {} : { address }),
        ...(valueAmount == null ? {} : { value: valueAmount }),
        ...(txid == null ? {} : { txid }),
        ...(vout == null ? {} : { vout }),
        ...(coinbase == null ? {} : { coinbase }),
    };
}
function parseTransactionOutput(value, path) {
    const object = expectObject(value, path);
    const address = expectOptionalString(readOptionalField(object, "address"), `${path}.address`);
    return {
        n: expectNumber(readField(object, "n", path), `${path}.n`),
        value: expectNumber(readField(object, "value", path), `${path}.value`),
        scriptPubKey: parseTransactionScriptPubKey(readField(object, "scriptPubKey", path), `${path}.scriptPubKey`),
        ...(address == null ? {} : { address }),
        spent: expectBoolean(readField(object, "spent", path), `${path}.spent`),
    };
}
function parseTransactionSummary(value, path) {
    const object = expectObject(value, path);
    return {
        txHash: expectString(readField(object, "txHash", path), `${path}.txHash`),
        blockHeight: expectNumber(readField(object, "blockHeight", path), `${path}.blockHeight`),
        blockPos: expectNumber(readField(object, "blockPos", path), `${path}.blockPos`),
        txVersion: expectNumber(readField(object, "txVersion", path), `${path}.txVersion`),
        txType: expectString(readField(object, "txType", path), `${path}.txType`),
        lockTime: expectNumber(readField(object, "lockTime", path), `${path}.lockTime`),
        txSize: expectNumber(readField(object, "txSize", path), `${path}.txSize`),
        valueOut: expectNumber(readField(object, "valueOut", path), `${path}.valueOut`),
        inputCount: expectNumber(readField(object, "inputCount", path), `${path}.inputCount`),
        outputCount: expectNumber(readField(object, "outputCount", path), `${path}.outputCount`),
        tags: parseTransactionTags(readField(object, "tags", path), `${path}.tags`),
    };
}
function parseTransactionDetail(value, path) {
    const object = expectObject(value, path);
    const asOfHeight = expectOptionalNumber(readOptionalField(object, "asOfHeight"), `${path}.asOfHeight`);
    const blockHeight = expectOptionalNumber(readOptionalField(object, "blockHeight"), `${path}.blockHeight`);
    const blockPos = expectOptionalNumber(readOptionalField(object, "blockPos"), `${path}.blockPos`);
    const fee = expectOptionalNumber(readOptionalField(object, "fee"), `${path}.fee`);
    const payload = readOptionalField(object, "payload");
    return {
        ...(asOfHeight == null ? {} : { asOfHeight }),
        txHash: expectString(readField(object, "txHash", path), `${path}.txHash`),
        ...(blockHeight == null ? {} : { blockHeight }),
        ...(blockPos == null ? {} : { blockPos }),
        txVersion: expectNumber(readField(object, "txVersion", path), `${path}.txVersion`),
        txType: expectString(readField(object, "txType", path), `${path}.txType`),
        lockTime: expectNumber(readField(object, "lockTime", path), `${path}.lockTime`),
        txSize: expectNumber(readField(object, "txSize", path), `${path}.txSize`),
        valueOut: expectNumber(readField(object, "valueOut", path), `${path}.valueOut`),
        inputCount: expectNumber(readField(object, "inputCount", path), `${path}.inputCount`),
        outputCount: expectNumber(readField(object, "outputCount", path), `${path}.outputCount`),
        ...(fee == null ? {} : { fee }),
        tags: parseTransactionTags(readField(object, "tags", path), `${path}.tags`),
        live: parseTransactionLive(readField(object, "live", path), `${path}.live`),
        ...(payload == null ? {} : { payload }),
        vin: parseInputs(readField(object, "vin", path), `${path}.vin`),
        vout: parseOutputs(readField(object, "vout", path), `${path}.vout`),
    };
}
function parseInputs(value, path) {
    return expectArray(value, path, parseTransactionInput);
}
function parseOutputs(value, path) {
    return expectArray(value, path, parseTransactionOutput);
}
function createCursorQuery(params) {
    if (params == null) {
        return undefined;
    }
    const query = createQuery({
        cursor: params.cursor,
        limit: params.limit,
    });
    return query.size > 0 ? query : undefined;
}
/**
 * Build the transaction route family for one client transport context.
 */
export function buildTransactionRoutes(context) {
    return {
        async get(txid) {
            return requestJsonResult(context, TRANSACTION_ROUTE, `/api/v1/tx/${encodePathSegment(txid)}`, parseTransactionDetail);
        },
        async list(params) {
            const query = createCursorQuery(params);
            return requestJsonResult(context, TRANSACTIONS_ROUTE, TRANSACTIONS_ROUTE.path, (value, path) => parseIndexedPage(value, path, parseTransactionSummary), ...(query == null ? [] : [{ query }]));
        },
    };
}
//# sourceMappingURL=transactions.js.map